import React, { useEffect } from 'react';
import { Row, Col } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import editFieldEventHandler, {
  CREATABLE_DROPDOWN_EVENT,
} from 'services/objects/editFieldEventHandler';
import IWall from 'components/interfaces/IWall';
import IWallMetaData from 'components/interfaces/IWallMetaData';
import CreatableDropdown from 'components/layout/forms/CreatableDropdown';
import NumberInputField from 'components/layout/forms/NumberInputField';
import showDirectionOptions from 'components/layout/forms/showDirectionOptions';
import TextInputField from 'components/layout/forms/TextInputField';
import translations from 'services/translations/translations';

import directions from '../../types/directions';
import WallTypeInput from './WallTypeInput';

const WallProperties = ({
  action,
  className,
  setWall,
  setWallMetaData,
  wall,
  wallMetaData,
  disabledInputs,
}: {
  action: string;
  className?: string;
  setWall: (wallToSet: IWall) => void;
  setWallMetaData: (wallMetaDataToSet: IWallMetaData) => void;
  wall: IWall;
  wallMetaData: IWallMetaData;
  disabledInputs?: boolean;
}): JSX.Element | null => {
  const { t } = useTranslation([translations.common]);

  const handleWall = editFieldEventHandler(wall, setWall);

  const handleCreatable = editFieldEventHandler(
    wall,
    setWall,
    CREATABLE_DROPDOWN_EVENT,
    'wallDirection'
  );

  const handleWallMetaData = editFieldEventHandler(
    wallMetaData,
    setWallMetaData
  );

  const nameInput = (
    <TextInputField
      id="name"
      label={t(`${translations.common}:Name`)}
      onChange={handleWallMetaData}
      value={wallMetaData.name}
      name="name"
      required
      className={className}
      disabled={disabledInputs}
    />
  );

  const wallTypeInput = (
    <WallTypeInput
      className={className}
      handleWall={handleWall}
      wall={wall}
      disabled={disabledInputs}
    />
  );

  const lengthInput = (
    <NumberInputField
      id="length"
      label={t(`${translations.common}:Length`)}
      onChange={handleWall}
      value={wall.length}
      name="length"
      required
      className={className}
      disabled={disabledInputs}
    />
  );

  const thicknessInput = (
    <NumberInputField
      id="thickness"
      label={t(`${translations.common}:Wall Properties.Thickness`)}
      onChange={handleWall}
      value={wall.thickness}
      name="thickness"
      required
      className={className}
      disabled={disabledInputs}
    />
  );

  const wallDirectionInput = (
    <CreatableDropdown
      label={t(`${translations.common}:Wall Properties.Wall Direction`)}
      placeholder=""
      name="wallDirection"
      value={{
        label: wall.wallDirection,
        value: wall.wallDirection,
      }}
      onChange={handleCreatable}
      options={showDirectionOptions(directions, translations.common, t)}
      disabled={disabledInputs}
    />
  );

  const roofHeightInput = (
    <NumberInputField
      id="roofHeight"
      label={t(`${translations.common}:Wall Properties.Roof Height`)}
      onChange={handleWall}
      value={wall.roofHeight}
      name="roofHeight"
      required
      className={className}
      disabled={disabledInputs}
    />
  );

  if (action === 'add') {
    return (
      <>
        <Row>
          <Col>{nameInput}</Col>
        </Row>
        <Row>
          <Col>{wallTypeInput}</Col>
        </Row>
        <Row>
          <Col>{lengthInput}</Col>
          <Col>{thicknessInput}</Col>
        </Row>
        <Row>
          <Col>{wallDirectionInput}</Col>
          <Col>{roofHeightInput}</Col>
        </Row>
      </>
    );
  }

  return (
    <>
      {nameInput}
      {wallTypeInput}
      {lengthInput}
      {thicknessInput}
      {wallDirectionInput}
      {roofHeightInput}
    </>
  );
};

export default WallProperties;
